<template>
  <div class="payment-rules-page" data-testid="payroll-payment-rules-page">
    <div class="page-header">
      <h1>{{ t('Regras de pagamento') }}</h1>
      <div class="header-actions">
        <Button
          data-testid="payroll-payment-rules-summary-button"
          :label="t('Resumo')"
          icon="pi pi-chart-bar"
          severity="secondary"
          outlined
          @click="openSummaryModal"
        />
        <Button
          v-if="canManagePaymentRules"
          data-testid="payroll-payment-rules-create-button"
          :label="t('Nova regra')"
          icon="pi pi-plus"
          @click="openCreateModal"
        />
      </div>
    </div>

    <box type="primary">
      <!-- Payment Rules Table - Shows all rules from filtered franchises -->
      <PaymentRulesTable
        @edit="openEditModal"
        @deactivate="handleDeactivate"
      />
    </box>

    <!-- PaymentRulesModal is centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { usePayrollPermissions } from '@/composables/usePayrollPermissions';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface PaymentRule {
  id: Id;
  [key: string]: unknown;
}

const { t } = useI18n();
const { deactivateRule } = usePaymentRules();
const { canManagePaymentRules } = usePayrollPermissions();

const handleSaved = (): void => {
  log('PaymentRulesPage', 'handleSaved', 'Rule saved');
  eventBus.emit('globalMessage.success', {
    message: t('Regra de pagamento salva com sucesso'),
    duration: 3000
  });
};

onMounted(() => {
  log('PaymentRulesPage', 'onMounted', 'Page mounted');
  eventBus.on('payment-rule.saved', handleSaved);
});

onBeforeUnmount(() => {
  log('PaymentRulesPage', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('payment-rule.saved', handleSaved);
});

const openCreateModal = (): void => {
  log('PaymentRulesPage', 'openCreateModal', 'Opening create modal via eventBus');
  eventBus.emit('modal.payment-rules.open', { rule: null });
};

const openSummaryModal = (): void => {
  log('PaymentRulesPage', 'openSummaryModal', 'Opening summary modal via eventBus');
  eventBus.emit('modal.payment-rules-summary.open');
};

const openEditModal = (rule: PaymentRule): void => {
  log('PaymentRulesPage', 'openEditModal', 'Opening edit modal via eventBus', { ruleId: rule.id });
  eventBus.emit('modal.payment-rules.open', { rule });
};

const handleDeactivate = async (rule: PaymentRule): Promise<void> => {
  try {
    await deactivateRule(Number(rule.id));
    eventBus.emit('payment-rule.deactivated', rule.id);
    eventBus.emit('globalMessage.success', {
      message: t('Regra de pagamento desativada com sucesso'),
      duration: 3000
    });
  } catch (error) {
    const err = error as Error;
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Falha ao desativar regra de pagamento'),
      duration: 5000
    });
  }
};
</script>

<style scoped>
.payment-rules-page {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
</style>
